import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../hooks/useApi';
import ArcaCertificado from './ArcaCertificado';
import ArcaComprobantes from './ArcaComprobantes';

// Vinculación fiscal con ARCA. Vive al lado de la integración de pagos porque son la misma
// decisión vista de dos lados: quién cobra es quien tiene que facturar.
//
// Esta pantalla NO emite comprobantes: carga lo que hace falta para poder emitirlos. Por eso
// se puede usar antes de tener el trámite listo, y por eso el interruptor arranca apagado.

// Qué entradas se facturan. Es la primera pregunta que hace un contador, y la respuesta
// no es sí o no: muchos boliches facturan la venta online (que cobra el boliche) y no la
// de puerta o RRPP, que se maneja por otra vía.
const ENTRADAS = [
  ['no', 'No facturar entradas', 'Ninguna venta de entradas emite comprobante'],
  ['online', 'Solo la venta online', 'Las que se compran por la web. Puerta y RRPP quedan afuera'],
  ['todas', 'Todas las entradas', 'Online, RRPP y boletería'],
];

const CONDICIONES = [
  ['RI', 'Responsable inscripto', 'Emite Factura B a consumidor final'],
  ['MONOTRIBUTO', 'Monotributista', 'Emite Factura C'],
  ['EXENTO', 'Exento', 'Emite Factura C'],
];

// 30123456789 → 30-12345678-9, solo para mostrar.
const linda = (c) => {
  const s = String(c || '').replace(/\D/g, '');
  return s.length === 11 ? `${s.slice(0, 2)}-${s.slice(2, 10)}-${s.slice(10)}` : String(c || '');
};

export default function Facturacion() {
  const api = useApi();
  const [st, setSt] = useState(null);
  const [f, setF] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api.get('/api/org/arca')
      .then(d => {
        setSt(d);
        setF({
          cuit: linda(d.cuit), razon_social: d.razon_social, condicion: d.condicion,
          condicion_desde: d.condicion_desde, punto_venta: d.punto_venta || '',
          entradas: d.entradas || 'no', recargo_qr: String(d.recargo_qr ?? 0),
        });
      })
      .catch(() => setSt(null))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setF(s => ({ ...s, [k]: v }));

  async function guardar() {
    setBusy(true); setErr(''); setMsg('');
    try {
      await api.put('/api/org/arca', {
        cuit: f.cuit, razon_social: f.razon_social, condicion: f.condicion,
        condicion_desde: f.condicion_desde, punto_venta: f.punto_venta,
        recargo_qr: f.recargo_qr === '' ? 0 : Number(f.recargo_qr),
      });
      setMsg('Datos guardados.'); load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }

  // El alcance se guarda al elegirlo: es un interruptor, no un formulario. Si falta
  // configuración, el backend lo rechaza y se explica en vez de quedar en un estado falso.
  async function elegirEntradas(v) {
    setBusy(true); setErr(''); setMsg('');
    try {
      await api.put('/api/org/arca', { entradas: v });
      load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }

  async function desvincular() {
    if (!window.confirm('¿Desvincular ARCA?\n\nSe borran el CUIT, la condición fiscal y el punto de venta, y se apaga la facturación. Los comprobantes ya emitidos no se tocan.')) return;
    setBusy(true); setErr('');
    try {
      await api.put('/api/org/arca', { cuit: '', razon_social: '', condicion: '', condicion_desde: '', punto_venta: '', entradas: 'no' });
      setMsg('ARCA desvinculada.'); load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }

  if (loading || !f) return <div style={{ color: 'var(--ink-600)', fontSize: 13 }}>Cargando…</div>;

  return (
    <div className="lb-fade" style={{ maxWidth: 620 }}>
      <div className="lb-eyebrow" style={{ marginBottom: 6 }}>Facturación electrónica</div>
      <p style={{ color: 'var(--ink-700)', fontSize: 14, marginTop: 0, marginBottom: 20, lineHeight: 1.6 }}>
        Conectá tu CUIT para que TukiPro emita las facturas de tus ventas online ante ARCA.
        El cumplimiento fiscal es responsabilidad tuya: TukiPro emite en tu nombre y por tu instrucción.
      </p>

      {msg && <div style={{ border: '1px solid rgba(46,204,113,0.5)', background: 'rgba(46,204,113,0.08)', borderRadius: 6, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#2ecc71' }}>{msg}</div>}
      {err && <div className="lb-err" style={{ marginBottom: 16 }}>{err}</div>}

      <div className="lb-card" style={{ padding: '24px 26px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ width: 40, height: 40, borderRadius: 8, background: 'var(--ink-200)', display: 'grid', placeItems: 'center', fontSize: 19 }}>🧾</div>
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>ARCA</div>
            <div style={{ fontSize: 12, color: st.enabled ? '#2ecc71' : 'var(--ink-600)' }}>
              {st.enabled ? 'Facturando' : st.completo ? 'Configurada, sin facturar' : 'Sin configurar'}
            </div>
          </div>
        </div>

        <Campo label="CUIT" nota="Se valida el dígito verificador al guardar.">
          <input className="lb-input" value={f.cuit} onChange={e => set('cuit', e.target.value)} placeholder="30-12345678-9" inputMode="numeric" />
        </Campo>
        <Campo label="Razón social">
          <input className="lb-input" value={f.razon_social} onChange={e => set('razon_social', e.target.value)} placeholder="Como figura en ARCA" />
        </Campo>

        <Campo label="Condición frente al IVA" nota="Define qué comprobante se emite.">
          <select className="lb-select" value={f.condicion} onChange={e => set('condicion', e.target.value)} style={{ width: '100%' }}>
            <option value="">— Elegir —</option>
            {CONDICIONES.map(([k, t, d]) => <option key={k} value={k}>{t} · {d}</option>)}
          </select>
        </Campo>

        {/* La fecha de vigencia no es un dato de trámite: es lo que hace que los
            comprobantes anteriores a un cambio de condición queden como se emitieron. */}
        <Campo label="Desde cuándo tenés esa condición" nota="Si cambiaste de monotributo a responsable inscripto, poné la fecha del pase. Antes de esa fecha se emitía otro tipo de comprobante y así no se mezclan.">
          <input className="lb-input" type="date" value={f.condicion_desde} onChange={e => set('condicion_desde', e.target.value)} />
        </Campo>

        {/* La ayuda anterior decía «uno dado de alta como Web Service» y se leía como si el
            web service fuera algo que hay que pedirle a alguien. Es una opción del
            desplegable al crear el punto de venta, así que ahora dice dónde se hace. */}
        <Campo label="Punto de venta">
          <input className="lb-input" value={f.punto_venta} inputMode="numeric" onChange={e => set('punto_venta', e.target.value.replace(/\D/g, '').slice(0, 5))} placeholder="Ej: 5" />
          <details style={{ marginTop: 6 }}>
            <summary style={{ fontSize: 11.5, color: 'var(--ink-700)', cursor: 'pointer' }}>¿Qué es y cómo lo saco?</summary>
            <div style={{ fontSize: 11.5, color: 'var(--ink-600)', marginTop: 8, lineHeight: 1.65 }}>
              Es el número que identifica desde dónde se emiten tus comprobantes: cada factura
              queda identificada por tipo, punto de venta y número.
              <div style={{ margin: '8px 0' }}>
                Lo das de alta vos en ARCA, en cinco minutos: entrás con tu clave fiscal a
                <b style={{ color: 'var(--ink-800)' }}> Administración de puntos de venta y domicilios</b> →
                <b style={{ color: 'var(--ink-800)' }}> A/B/M de Puntos de Venta</b> → Agregar. En
                «Sistema» elegís la opción de <b style={{ color: 'var(--ink-800)' }}>Web Services</b> que
                corresponda a tu condición — para responsable inscripto es «RECE para aplicativo
                y Web Services».
              </div>
              <b style={{ color: 'var(--ink-800)' }}>Tiene que ser uno nuevo, sin uso.</b> El que usás
              en Comprobantes en Línea no sirve: cada punto de venta lleva su propia numeración
              y no se pueden mezclar.
            </div>
          </details>
        </Campo>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 20 }}>
          <button className="lb-btn lb-btn--white lb-btn--sm" disabled={busy} onClick={guardar}>{busy ? 'Guardando…' : 'Guardar datos'}</button>
          {st.cuit && <button className="lb-btn lb-btn--ghost lb-btn--sm" style={{ color: 'var(--red-400)' }} disabled={busy} onClick={desvincular}>Desvincular</button>}
        </div>
      </div>

      {/* El alcance va aparte de los datos, y a propósito: cargar el CUIT no puede
          significar que ya se esté facturando. Elegir qué se factura es deliberado. */}
      <div className="lb-card" style={{ padding: '22px 26px', marginTop: 16 }}>
        <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>Qué entradas se facturan</div>
        <div style={{ fontSize: 12.5, color: 'var(--ink-600)', margin: '4px 0 14px', lineHeight: 1.5 }}>
          {st.completo
            ? 'Se puede excluir un evento puntual desde la configuración de ese evento — por ejemplo una noche que cobra un socio.'
            : 'Faltan el CUIT, la condición fiscal y el punto de venta para poder facturar.'}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {ENTRADAS.map(([k, t, d]) => {
            const on = (f.entradas || 'no') === k;
            return (
              <button key={k} type="button" disabled={busy || (!st.completo && k !== 'no')}
                onClick={() => { set('entradas', k); elegirEntradas(k); }}
                style={{ textAlign: 'left', cursor: 'pointer', padding: '12px 14px', borderRadius: 4,
                  background: on ? 'rgba(255,255,255,0.06)' : 'var(--ink-050)',
                  border: '1px solid ' + (on ? 'rgba(255,255,255,0.5)' : 'var(--border)'),
                  opacity: (!st.completo && k !== 'no') ? 0.5 : 1 }}>
                <div style={{ color: on ? '#fff' : 'var(--ink-800)', fontWeight: 600, fontSize: 13 }}>{t}</div>
                <div style={{ color: 'var(--ink-600)', fontSize: 11.5, marginTop: 2 }}>{d}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* COBRO EN LAS CAJAS. No es fiscal —el recargo se cobra igual aunque no se facture—
          pero se configura acá porque es la otra mitad de la misma conversación: el cajero
          elige efectivo o QR, y de ahí sale qué se factura en cada puesto. */}
      <div className="lb-card" style={{ padding: '22px 26px', marginTop: 16 }}>
        <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>Recargo por pagar con QR</div>
        <div style={{ fontSize: 12.5, color: 'var(--ink-600)', margin: '4px 0 12px', lineHeight: 1.5 }}>
          Al cobrar, el cajero elige efectivo o QR. Si ponés un porcentaje, el pago con QR lo suma
          y el ticket lo muestra como línea aparte. Rige para todas las cajas del boliche. 0 = sin recargo.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, maxWidth: 220 }}>
          <input className="lb-input" inputMode="decimal" value={f.recargo_qr}
            onChange={e => set('recargo_qr', e.target.value.replace(/[^\d.]/g, '').slice(0, 5))} placeholder="0" />
          <span style={{ color: 'var(--ink-700)', fontSize: 14 }}>%</span>
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--ink-600)', marginTop: 8 }}>Se guarda con el botón «Guardar datos» de arriba.</div>
        <div style={{ fontSize: 11.5, color: 'var(--ink-600)', marginTop: 10, lineHeight: 1.6 }}>
          Qué factura cada puesto (solo el QR o también el efectivo) se elige en <b style={{ color: 'var(--ink-800)' }}>Cajas TPV</b>, en cada puesto.
        </div>
      </div>

      <ArcaCertificado onCambio={load} />
      <ArcaComprobantes />

      <div style={{ fontSize: 11.5, color: 'var(--ink-600)', marginTop: 14, lineHeight: 1.6 }}>
        Con la facturación apagada el sistema funciona igual que siempre: se sigue vendiendo y cobrando, solo que no se emiten comprobantes.
      </div>
    </div>
  );
}

function Campo({ label, nota, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div className="lb-label" style={{ marginBottom: 5 }}>{label}</div>
      {children}
      {nota && <div style={{ fontSize: 11.5, color: 'var(--ink-600)', marginTop: 5, lineHeight: 1.5 }}>{nota}</div>}
    </div>
  );
}
